import { MutableRefObject, useEffect, useMemo, useRef } from "react";
import { buffer } from "ol/extent";
import { Vector as VectorSource } from "ol/source";
import { Vector as VectorLayer } from "ol/layer";
import { Feature, Map } from "ol";
import { Point } from "ol/geom";
import { fromLonLat } from "ol/proj";
import { Coordinate } from "ol/coordinate";
import {
  IMammadabaMapProps,
  OnFeatureClickCallback,
  OnUnselectCallback,
  IPadding,
} from "./mapProvider";
import { SelectedStatus } from "./enums";
import { config } from "./config";
import { useAppContext } from "./AppContext";
import {
  createMap,
  forEachFeatureOfLayer,
  getNearbyCircle,
  handleClusterClick,
  onPointerMove,
  selectTrack,
  zoomToFeature,
} from "./mapFunctions";
import { FilterOptions } from "./filters";
import { FeatureLike } from "ol/Feature";

const centerPoint = new Point([
  config.defaults.center[0],
  config.defaults.center[1],
]);
const _padding: IPadding = { left: 0, duration: 0 };
const clusterLayerId = config.defaults.clusterLayerId;
const tracksLayerId = config.defaults.tracksLayerId;

const getBuffer = (feature: Feature) =>
  buffer(feature.getGeometry().getExtent(), 100);

let currentPosition = undefined;
/**
 * @returns current position, as [longitude,latitude]
 */
export const getCurrentPosition = (): number[] | undefined => {
  return currentPosition;
};

let _map: MutableRefObject<Map>;
let allFeaturesRef: MutableRefObject<Feature[]>;
let sourceRef: MutableRefObject<VectorSource>;
let selectedSourceRef: MutableRefObject<VectorSource>;
let filterOptionsRef: MutableRefObject<FilterOptions>;

let onFeatureClick: MutableRefObject<OnFeatureClickCallback>;
let onUnselect: MutableRefObject<OnUnselectCallback>;

export const useMap = (
  target: MutableRefObject<HTMLDivElement>,
  props: IMammadabaMapProps
): Map => {
  _map = useRef<Map>(null);
  sourceRef = useRef<VectorSource>(new VectorSource());
  selectedSourceRef = useRef<VectorSource>(new VectorSource());
  allFeaturesRef = useRef<Feature[]>();
  filterOptionsRef = useRef<FilterOptions>(props.filterOptions);

  onFeatureClick = useRef<OnFeatureClickCallback>(props.onClickOnFeature);
  onFeatureClick.current = useMemo(
    () => props.onClickOnFeature,
    [props.onClickOnFeature]
  );
  onUnselect = useRef<OnUnselectCallback>(props.onUnselect);
  onUnselect.current = useMemo(() => props.onUnselect, [props.onUnselect]);

  const nearbyCircle = useRef<Feature>();
  const nearbyCircleLayer = useRef<VectorLayer<VectorSource>>();

  const { texts, setMapClick } = useAppContext();

  useEffect(() => {
    if (target.current) {
      _map.current = createMap(
        target.current,
        sourceRef.current,
        texts,
        props.startPosition
      );

      _map.current.on("pointermove", (evt) =>
        onPointerMove(evt, _map.current, target.current)
      );

      _map.current.on("singleclick", (evt) => {
        if (evt.dragging) {
          return;
        }
        const hasFeatureAtPixel = _map.current.hasFeatureAtPixel(evt.pixel, {
          layerFilter: (layer) => layer.get("id") === clusterLayerId,
        });

        if (hasFeatureAtPixel) {
          _map.current.forEachFeatureAtPixel(
            evt.pixel,
            (f, layer, geometry) => {
              evt.coordinate = geometry.getCoordinates();
              handleObjectClick(f);
            },
            {
              layerFilter: (layer) => layer.get("id") === clusterLayerId,
            }
          );
        } else {
          resetSelectState();
        }
        setMapClick(evt);
      });

      _map.current.on("dblclick", (evt) => {
        if (evt.dragging) {
          return;
        }
        const hasFeatureAtPixel = _map.current.hasFeatureAtPixel(evt.pixel, {
          layerFilter: (layer) => layer.get("id") === clusterLayerId,
        });

        if (hasFeatureAtPixel) {
          evt.stopPropagation();
          _map.current.forEachFeatureAtPixel(evt.pixel, handleDoubleClick, {
            layerFilter: (layer) => layer.get("id") === clusterLayerId,
          });
        } else {
          resetSelectState();
        }
      });

      _map.current.on("moveend", () =>
        document.dispatchEvent(new CustomEvent("moveend"))
      );
      _map.current.on("movestart", () =>
        document.dispatchEvent(new CustomEvent("movestart"))
      );
      // @ts-ignore
      window.MAP = _map;

      window.onresize = () =>
        setTimeout(function () {
          _map.current.updateSize();
        }, 200);

      window.visualViewport.onresize = () =>
        setTimeout(function () {
          _map.current.updateSize();
        }, 300);

      const handleObjectClick = (feature: FeatureLike) => {
        const clusteredFeatures: Feature[] = feature.get("features");
        if (clusteredFeatures.length) {
          if (clusteredFeatures.length === 1) {
            if (
              clusteredFeatures[0].get("SELECTED") === SelectedStatus.Default
            ) {
              resetSelectState();
              clusteredFeatures[0].set("SELECTED", SelectedStatus.Selected);
              selectTrack(clusteredFeatures[0].get("OBJECTID"));
            } else {
              const mapSize = _map.current.getSize();
              const view = _map.current.getView();
              view.fit(getBuffer(clusteredFeatures[0]), {
                padding: [mapSize[1] * 0.5, 0, 0, view.get("paddingLeft")],
                duration: 500,
              });
            }
            onFeatureClick.current?.(clusteredFeatures[0]);
          } else {
            handleClusterClick(feature as Feature);
          }
        } else {
          resetSelectState();
        }
      };

      const handleDoubleClick = (feature: Feature) => {
        if (feature.get("features").length !== 1) {
          handleClusterClick(feature.get("features"));
          return;
        }
        const view = _map.current.getView();
        view.fit(getBuffer(feature), {
          padding: [0, 0, 0, view.get("paddingLeft")],
          duration: 500,
        });
      };
    }
    document.addEventListener(
      "currentPosition",
      (e: CustomEvent) => (currentPosition = e.detail)
    );
  }, []);

  useEffect(() => {
    sourceRef.current.clear();
    props.allFeatures && sourceRef.current.addFeatures(props.allFeatures);
    allFeaturesRef.current = props.allFeatures;
  }, [props.allFeatures]);

  useEffect(() => {
    if (props.features && props.features.length) {
      sourceRef.current.clear();
      sourceRef.current.addFeatures(props.features);
    } else {
      sourceRef.current.clear();
    }
  }, [props.features]);

  useEffect(() => {
    props.zoomTo && zoomToFeature(props.zoomTo);
  }, [props.zoomTo]);

  useEffect(() => {
    props.nearbyCircleVisible
      ? addNearbyCircle(nearbyCircle, nearbyCircleLayer)
      : removeNearbyCircle(nearbyCircle, nearbyCircleLayer);
  }, [props.nearbyCircleVisible]);

  useEffect(() => {
    filterOptionsRef.current = props.filterOptions;
  }, [props.filterOptions]);

  useEffect(() => {
    _padding.left =
      props.padding.left === 0 && _padding.left > 0
        ? -_padding.left
        : props.padding.left > 0
        ? props.padding.left
        : 0;
    const view = _map.current.getView();
    const maxZoom = view.getZoom();
    const center = view.getCenter();
    centerPoint.setCoordinates(center);
    view.fit(centerPoint, {
      padding: [0, 0, 0, _padding.left],
      maxZoom,
      duration: props.padding.duration,
      callback: () => {
        view.set("paddingLeft", _padding.left < 0 ? 0 : _padding.left);
      },
    });
  }, [props.padding.left, props.padding.duration]);

  return _map.current;
};

const addNearbyCircle = async (
  nearbyCircle: MutableRefObject<Feature>,
  nearbyCircleLayer: MutableRefObject<VectorLayer<VectorSource>>
) => {
  if (nearbyCircleLayer.current) {
    return;
  }
  const location = await getLocation();

  nearbyCircle.current = getNearbyCircle(location);
  nearbyCircleLayer.current = new VectorLayer({
    map: _map.current,
    source: new VectorSource({
      features: [nearbyCircle.current],
    }),
  });
};

const removeNearbyCircle = (
  nearbyCircle: MutableRefObject<Feature>,
  nearbyCircleLayer: MutableRefObject<VectorLayer<VectorSource>>
) => {
  nearbyCircleLayer.current && nearbyCircleLayer.current.getSource().clear();
  _map.current.removeLayer(nearbyCircleLayer.current);
  nearbyCircleLayer.current = null;
  nearbyCircle.current = null;
};

const getLocation = async (): Promise<Coordinate | undefined> => {
  return new Promise((resolve) => {
    currentPosition && currentPosition.length
      ? resolve(currentPosition)
      : navigator.geolocation.getCurrentPosition(
          (e) =>
            resolve(
              (currentPosition = fromLonLat([
                e.coords.longitude,
                e.coords.latitude,
              ]))
            ),
          () => resolve(undefined)
        );
  });
};

const resetSelectState = () => {
  const layers = _map.current.getLayers().getArray();
  for (const layer of layers) {
    // reset tracks
    forEachFeatureOfLayer(tracksLayerId, (f: Feature) =>
      f.set("SELECTED", SelectedStatus.Default)
    );
    // reset object features
    for (const feature of allFeaturesRef.current) {
      if (feature.get("SELECTED") !== SelectedStatus.Default) {
        feature.set("SELECTED", SelectedStatus.Default);
        onUnselect.current?.(feature.get("OBJECTID"));
      }
    }
    selectedSourceRef.current.clear();
  }
};
